import React, { useState } from 'react';
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Typography,
    styled
} from '@mui/material';

import {
    EXPORT_SECTIONS,
    DOCUMENT_PARTS,
    defaultSectionSelection,
    defaultImageParts
} from 'employee/utils/employeePdfSchema';

/**
 * Shared state + fields for the PDF export dialogs (single employee AND the
 * whole-directory export): report type, section toggles and document-image
 * parts. Keeping this in one place guarantees both dialogs stay identical.
 */

export const OptionGrid = styled(Box)(({ theme: { spacing } }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: spacing(1),
    '@media screen and (max-width: 480px)': {
        gridTemplateColumns: '1fr'
    }
}));

export const GroupLabel = ({ children }) => (
    <Typography
        variant='subtitle2'
        sx={{ fontWeight: 600, mt: 2, mb: 0.5, textAlign: 'left' }}
    >
        {children}
    </Typography>
);

const DETAIL_OPTIONS = [
    { value: 'detailed', label: 'Detailed', hint: 'Every field on record' },
    { value: 'summary', label: 'Summary', hint: 'Key information only' }
];

export const useExportOptions = () => {
    const [detail, setDetail] = useState('detailed');
    const [sections, setSections] = useState(defaultSectionSelection);
    const [includeImages, setIncludeImages] = useState(true);
    const [imageParts, setImageParts] = useState(defaultImageParts);

    const selectedCount = Object.values(sections).filter(Boolean).length;
    const allSelected = selectedCount === EXPORT_SECTIONS.length;

    const toggleSection = (key) =>
        setSections((prev) => ({ ...prev, [key]: !prev[key] }));

    const toggleAllSections = () => {
        const next = !allSelected;
        setSections(
            EXPORT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: next }), {})
        );
    };

    const togglePart = (key) =>
        setImageParts((prev) => ({ ...prev, [key]: !prev[key] }));

    return {
        detail,
        setDetail,
        sections,
        toggleSection,
        toggleAllSections,
        includeImages,
        setIncludeImages,
        imageParts,
        togglePart,
        selectedCount,
        allSelected,
        /** The options object the pdf generators expect. */
        options: { detail, sections, includeImages, imageParts }
    };
};

export default function ExportOptionsFields({
    state,
    imagesLabel = 'Include document images',
    imagesHint = 'Photograph, signature, NID card and other attachments'
}) {
    const {
        detail,
        setDetail,
        sections,
        toggleSection,
        toggleAllSections,
        includeImages,
        setIncludeImages,
        imageParts,
        togglePart,
        selectedCount,
        allSelected
    } = state;

    return (
        <>
            <GroupLabel>Report type</GroupLabel>
            <RadioGroup
                row
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
            >
                {DETAIL_OPTIONS.map((opt) => (
                    <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={<Radio size='small' />}
                        sx={{ mr: 4, alignItems: 'flex-start', mt: 0.5 }}
                        label={
                            <Box>
                                <Typography variant='body2'>
                                    {opt.label}
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    {opt.hint}
                                </Typography>
                            </Box>
                        }
                    />
                ))}
            </RadioGroup>

            <Divider sx={{ my: 1.5 }} light />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <GroupLabel>
                    Sections ({selectedCount}/{EXPORT_SECTIONS.length})
                </GroupLabel>
                <FormControlLabel
                    sx={{ mr: 0, mt: 1.5 }}
                    control={
                        <Checkbox
                            size='small'
                            checked={allSelected}
                            indeterminate={selectedCount > 0 && !allSelected}
                            onChange={toggleAllSections}
                        />
                    }
                    label={<Typography variant='body2'>Select all</Typography>}
                />
            </Box>
            <OptionGrid>
                {EXPORT_SECTIONS.map((section) => (
                    <FormControlLabel
                        key={section.key}
                        control={
                            <Checkbox
                                size='small'
                                checked={!!sections[section.key]}
                                onChange={() => toggleSection(section.key)}
                            />
                        }
                        label={
                            <Typography variant='body2'>
                                {section.label}
                            </Typography>
                        }
                    />
                ))}
            </OptionGrid>

            <Divider sx={{ my: 1.5 }} light />

            <FormControlLabel
                control={
                    <Switch
                        size='small'
                        checked={includeImages}
                        onChange={(e) => setIncludeImages(e.target.checked)}
                    />
                }
                label={
                    <Box sx={{ ml: 0.5 }}>
                        <Typography variant='body2'>{imagesLabel}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                            {imagesHint}
                        </Typography>
                    </Box>
                }
            />
            {includeImages && (
                <OptionGrid sx={{ pl: 4, pt: 0.5 }}>
                    {DOCUMENT_PARTS.map((part) => (
                        <FormControlLabel
                            key={part.key}
                            control={
                                <Checkbox
                                    size='small'
                                    checked={!!imageParts[part.key]}
                                    onChange={() => togglePart(part.key)}
                                />
                            }
                            label={
                                <Typography variant='body2'>
                                    {part.label}
                                </Typography>
                            }
                        />
                    ))}
                </OptionGrid>
            )}
        </>
    );
}
